import {
  Button,
  FormGroup,
  FormWarning,
  Loader,
  PasswordStrength,
} from "@shltelemedicine/shl-ui-lib";
import { useEffect, useRef, useState } from "react";
import { Form } from "react-bootstrap";
import { useForm } from "react-hook-form";
import { FormattedMessage, useIntl } from "react-intl";
import { useNavigate } from "react-router-dom";
import passwordUpdatedIcon from "../../../assets/settings/password_updated.svg";
import { passwordPattern, RouteConstants } from "../../../constants";
import useResetPassword from "../../../hooks/useResetPassword";
import PasswordRules from "../../ChangePassword/PasswordRules/PasswordRules";
import AlertModal from "../../Shared/AlertModal/AlertModal";
import ConfirmationModal from "../../Shared/ConfirmationModal/ConfirmationModal";

import TextFormInput from "../../Shared/TextInput/TextFormInput";
import styles from "./ResetPasswordForm.module.css";
import { RedirectDestinations } from "../ResetPassword";

enum ResetPasswordFormKeys {
  newPassword = "newPassword",
  passwordConfirmation = "passwordConfirmation",
}
type ResetPasswordFormInputs = {
  [key in ResetPasswordFormKeys]: string;
};

type ResetPasswordFormProps = {
  token: string;
  userName: string;
  redirectDestination: RedirectDestinations;
  deeplink?: string;
  setShowSuccessMessage: React.Dispatch<React.SetStateAction<boolean>>;
};

const ResetPasswordForm = ({
  token,
  userName,
  redirectDestination,
  deeplink,
  setShowSuccessMessage,
}: ResetPasswordFormProps) => {
  let navigate = useNavigate();
  const intl = useIntl();

  const {
    handleSubmit,
    control,
    setError,
    formState: {
      errors: formErrors,
      isDirty,
      dirtyFields: { passwordConfirmation: isPasswordConfirmationDirty },
    },
    watch,
    reset,
  } = useForm<ResetPasswordFormInputs>();

  const {
    resetPassword,
    loading,
    alertProps,
    alertMessage,
    passwordSubmitted,
    setPasswordSubmitted,
    tokenInvalid,
  } = useResetPassword();

  useEffect(() => {
    reset();
  }, [reset, token]);

  const passwordRef = useRef<string>("");
  passwordRef.current = watch(ResetPasswordFormKeys.newPassword, "");

  const resetPasswordHandler = async ({
    newPassword,
  }: ResetPasswordFormInputs) => {
    const { success, errorCode } = await resetPassword(
      token ?? "",
      newPassword
    );
    if (!success && errorCode) {
      switch (errorCode) {
        case "400":
          setError("passwordConfirmation", {
            type: "pattern",
            message: intl.messages[
              "SETTINGS.VALID_PASSWORD_REQUIRED"
            ] as string,
          });
          break;
      }
    }
  };

  const [formDisabled, setFormDisabled] = useState(false);

  useEffect(() => {
    if (tokenInvalid) setFormDisabled(true);
  }, [tokenInvalid]);

  const handleDeeplinkNavigation = (deeplink: string) => {
    window.location.href = deeplink;
    setTimeout(() => {
      //show success message in case navigation via deeplink failed
      setShowSuccessMessage(true);
    }, 1000);
  };

  const handleConfirmationModal = () => {
    setPasswordSubmitted(false);
    switch (redirectDestination) {
      case RedirectDestinations.Message:
        !tokenInvalid && setShowSuccessMessage(true);
        return;
      case RedirectDestinations.Portal:
        navigate(RouteConstants.LOGIN_PATH, { replace: true });
        return;
      case RedirectDestinations.App:
        deeplink
          ? handleDeeplinkNavigation(deeplink)
          : setShowSuccessMessage(true);
        return;
      case RedirectDestinations.NoRedirection:
        setFormDisabled(true);
        return;
    }
  };

  return (
    <div className="container">
      <Form onSubmit={handleSubmit(resetPasswordHandler)}>
        {loading && (
          <div className="p-sm-2 mb-sm-3">
            <Loader />
          </div>
        )}
        {!loading && (
          <div className={`${styles.textInfo} align-items-center mb-2`}>
            <FormattedMessage
              id="RESET_PASSWORD.PASSWORD_RECOVERY"
              defaultMessage="Password Recovery"
            />
          </div>
        )}

        {/* Username */}
        <FormGroup
          label={
            <>
              <FormattedMessage id="USER.USERNAME" defaultMessage="Username" />:
            </>
          }
          input={
            <div className="text-md-center">
              <p className={`m-0 ${styles.email}`}>{userName}</p>
            </div>
          }
        />

        {/* Password */}
        <FormGroup
          required
          label={
            <FormattedMessage
              id="RESET_PASSWORD.PASSWORD"
              defaultMessage="Password"
            />
          }
          input={
            <TextFormInput
              name={"newPassword"}
              control={control}
              rules={{
                required: true,
                minLength: 8,
                pattern: passwordPattern,
              }}
              input={{
                type: "password",
                isInvalid: !!formErrors?.newPassword,
                disabled: loading || formDisabled,
                readOnly: formDisabled,
              }}
            />
          }
          validation={
            !!formErrors?.newPassword && (
              <FormWarning
                className="font-sm flex-grow-1 mt-2"
                message={
                  <FormattedMessage
                    id={`${
                      formErrors?.newPassword?.type === "pattern" ||
                      formErrors?.newPassword?.type === "minLength"
                        ? "SETTINGS.VALID_PASSWORD_REQUIRED"
                        : "SETTINGS.PASSWORD_REQUIRED"
                    }`}
                    defaultMessage={`${
                      formErrors?.newPassword?.type === "pattern" ||
                      formErrors?.newPassword?.type === "minLength"
                        ? "Insert Valid Password"
                        : "Insert Password"
                    }`}
                  />
                }
              />
            )
          }
        />

        {/* Retype Password */}
        <FormGroup
          required
          label={
            <FormattedMessage
              id="CHANGE_PASSWORD.RETYPE_PASSWORD"
              defaultMessage="Retype Password"
            />
          }
          input={
            <TextFormInput
              name={"passwordConfirmation"}
              control={control}
              rules={{
                required: true,
                validate: {
                  passwordMismatch: (value) => value === passwordRef.current,
                },
              }}
              input={{
                type: "password",
                isInvalid: !!formErrors?.passwordConfirmation,
                disabled: loading || formDisabled,
                readOnly: formDisabled,
              }}
            />
          }
          validation={
            !!formErrors?.passwordConfirmation && (
              <FormWarning
                className="font-sm flex-grow-1 mt-2"
                message={
                  <FormattedMessage
                    id={"SETTINGS.VALID_PASSWORD_REQUIRED"}
                    defaultMessage={"Insert Valid Password"}
                  />
                }
              />
            )
          }
        />

        {/* Password Strength */}
        <FormGroup
          label={
            <FormattedMessage
              id="PASSWORD_STRENGTH"
              defaultMessage="Password Strength"
            />
          }
          input={<PasswordStrength password={passwordRef.current} />}
        />

        <div className="mb-3">
          <PasswordRules />
        </div>

        <div className="d-flex justify-content-end px-1 mt-2">
          <Button
            type="submit"
            bgColor="transparent linear-gradient(180deg, #505F95 0%, #2C449A 100%) 0% 0% no-repeat padding-box"
            className="capitalized px-4"
            disabled={
              !isDirty ||
              !isPasswordConfirmationDirty ||
              loading ||
              formDisabled
            }
          >
            SUBMIT
          </Button>
        </div>
      </Form>
      {passwordSubmitted && (
        <ConfirmationModal
          show={passwordSubmitted}
          okBtn={{
            onClick: handleConfirmationModal,
          }}
          title={
            <FormattedMessage
              id="SETTINGS.PASSWORD_UPDATED"
              defaultMessage="Password Updated"
            />
          }
          onClose={handleConfirmationModal}
          headerIcon={passwordUpdatedIcon}
        >
          <FormattedMessage
            id="SETTINGS.PASSWORD_UPDATED_SUCCESSFULLY"
            defaultMessage="Password Updated Successfully"
          />
        </ConfirmationModal>
      )}
      <AlertModal {...alertProps}>{alertMessage}</AlertModal>
    </div>
  );
};

export default ResetPasswordForm;
